import { useEffect, useRef, useState, useCallback } from "react";

/**
 * RemoteCanvas — renders incoming JPEG frames to a canvas and forwards
 * mouse/keyboard input back via the provided sendInput callback.
 *
 * Block-E optimizations:
 *   - Auto-hiding toolbar (only shows on mouse-near-top)
 *   - Floating settings drawer from the right (doesn't cover stream content)
 *   - Decode pipeline uses createImageBitmap + frame-drop policy for low latency
 *   - Binary WebSocket frames supported as well as JSON-wrapped base64
 *   - requestAnimationFrame-based render scheduling to maximize browser frame budget
 *   - Real cursor (default arrow) — no more crosshair
 */
export default function RemoteCanvas({ ws, sendInput, remoteWidth, remoteHeight, initialFps, initialQuality }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);

  const [scale, setScale] = useState(1);
  const [fitMode, setFitMode] = useState("fit"); // "fit" | "actual"
  const [fps, setFps] = useState(0);
  const [showInputHint, setShowInputHint] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [toolbarPinned, setToolbarPinned] = useState(false);
  const [showToolbar, setShowToolbar] = useState(true);
  const [streamFps, setStreamFps] = useState(initialFps || 30);
  const [streamQuality, setStreamQuality] = useState(initialQuality || 70);
  const [remoteCursor, setRemoteCursor] = useState("default");
  const [monitors, setMonitors] = useState([]);  // [{index,width,height,x,y}]
  const [activeDisplays, setActiveDisplays] = useState([0]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [clipboardEnabled, setClipboardEnabled] = useState(true);
  const [clipboardStatus, setClipboardStatus] = useState(""); // "" | "synced" | "error"
  const lastSentClipRef = useRef("");
  const lastReceivedClipRef = useRef("");

  const frameCountRef = useRef(0);
  const pendingBitmapRef = useRef(null);  // next bitmap to draw
  const decodeBusyRef = useRef(false);
  const rafRef = useRef(0);
  const lastMouseMoveRef = useRef(0);
  const toolbarHideTimerRef = useRef(0);
  const lastClipboardRef = useRef("");

  // ── FPS counter ──
  useEffect(() => {
    const id = setInterval(() => {
      setFps(frameCountRef.current);
      frameCountRef.current = 0;
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // ── Canvas dimensions ──
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !remoteWidth || !remoteHeight) return;
    c.width = remoteWidth;
    c.height = remoteHeight;
  }, [remoteWidth, remoteHeight]);

  // ── Scale to fit ──
  useEffect(() => {
    function update() {
      const cont = containerRef.current;
      if (!cont || !remoteWidth || !remoteHeight) return;
      if (fitMode === "actual") { setScale(1); return; }
      const cw = cont.clientWidth;
      const ch = cont.clientHeight;
      setScale(Math.min(cw / remoteWidth, ch / remoteHeight));
    }
    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, [remoteWidth, remoteHeight, fitMode]);

  // ── RAF render loop: pull from pendingBitmap, draw, signal we're ready for more ──
  useEffect(() => {
    function tick() {
      rafRef.current = requestAnimationFrame(tick);
      const bitmap = pendingBitmapRef.current;
      if (!bitmap) return;
      pendingBitmapRef.current = null;
      const ctx = canvasRef.current?.getContext("2d", { alpha: false, desynchronized: true });
      if (!ctx) {
        bitmap.close && bitmap.close();
        return;
      }
      try {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(bitmap, 0, 0);
        frameCountRef.current++;
      } catch {}
      bitmap.close && bitmap.close();
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // ── Frame decode pipeline ──
  useEffect(() => {
    if (!ws) return;
    const supportsBitmap = typeof createImageBitmap === "function";
    // Tell browser to deliver binary frames as ArrayBuffer (faster than Blob)
    try { ws.binaryType = "arraybuffer"; } catch {}

    async function decode(blob) {
      if (!supportsBitmap) return null;
      try {
        // imageOrientation:none — don't apply EXIF rotation; faster
        return await createImageBitmap(blob, { imageOrientation: "none", premultiplyAlpha: "none" });
      } catch {
        return null;
      }
    }

    async function handleBytes(bytes, format) {
      // Drop if a decode is already running OR a pending bitmap is queued.
      // Always keep only the freshest frame to minimize input latency.
      if (decodeBusyRef.current) return;
      decodeBusyRef.current = true;
      try {
        const blob = new Blob([bytes], { type: `image/${format || "jpeg"}` });
        const bitmap = await decode(blob);
        if (bitmap) {
          // If RAF hasn't picked up the previous one yet, close it first
          if (pendingBitmapRef.current) {
            try { pendingBitmapRef.current.close && pendingBitmapRef.current.close(); } catch {}
          }
          pendingBitmapRef.current = bitmap;
        }
      } finally {
        decodeBusyRef.current = false;
      }
    }

    const handler = (event) => {
      // Binary path — raw JPEG bytes
      if (event.data instanceof ArrayBuffer) {
        handleBytes(new Uint8Array(event.data), "jpeg");
        return;
      }
      // JSON-wrapped path
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }

      if (msg.type === "browser.cursor") {
        const name = msg.payload?.name || msg.name || "default";
        console.log("[RemoteCanvas] cursor →", name);
        setRemoteCursor(name);
        return;
      }

      if (msg.type === "clipboard.set") {
        // Host sent us new clipboard content — write it to local clipboard.
        const text = msg.payload?.text ?? msg.text ?? "";
        if (!clipboardEnabled || typeof text !== "string") return;
        // Track what we just received so the outbound poller doesn't echo it back
        lastReceivedClipRef.current = text;
        lastSentClipRef.current = text;
        try {
          if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(
              () => setClipboardStatus("synced"),
              (err) => {
                console.warn("[clipboard] writeText failed:", err);
                setClipboardStatus("error");
              }
            );
          }
        } catch (err) {
          console.warn("[clipboard] write error:", err);
        }
        return;
      }

      if (msg.type === "clipboard.set") {
        // Host pushed remote-machine's clipboard. Write to local clipboard
        // so the user can paste in their own apps. Requires a user gesture
        // in most browsers — best-effort, silent on permission denial.
        const text = msg.payload?.text;
        if (typeof text === "string" && text.length > 0 && text !== lastClipboardRef.current) {
          lastClipboardRef.current = text;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).catch(() => {
              // Permission denied / not focused — just remember it for later
            });
          }
        }
        return;
      }

      if (msg.type === "browser.status") {
        const p = msg.payload || msg;
        if (Array.isArray(p.monitors)) setMonitors(p.monitors);
        if (Array.isArray(p.displays)) setActiveDisplays(p.displays);
        if (typeof p.fps === "number") setStreamFps(p.fps);
        if (typeof p.quality === "number") setStreamQuality(p.quality);
        return;
      }

      if (msg.type !== "browser.frame") return;
      const data = msg.payload?.data ?? msg.data;
      const format = msg.payload?.format ?? msg.format ?? "jpeg";
      if (!data) return;
      try {
        const bin = atob(data);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        handleBytes(arr, format);
      } catch {}
    };

    ws.addEventListener("message", handler);
    return () => ws.removeEventListener("message", handler);
  }, [ws]);

  // ── Clipboard sync (viewer → host) ──
  // When the wrapper is focused, periodically poll the local clipboard and
  // forward any changes to the host so paste shortcuts (Ctrl+V on the remote
  // machine) get the same text the user just copied locally.
  useEffect(() => {
    if (!sendInput) return;
    let cancelled = false;

    async function pushIfChanged() {
      if (cancelled) return;
      if (!navigator.clipboard || !navigator.clipboard.readText) return;
      try {
        const txt = await navigator.clipboard.readText();
        if (typeof txt !== "string" || !txt) return;
        if (txt === lastClipboardRef.current) return;
        if (txt.length > 256 * 1024) return; // sanity cap
        lastClipboardRef.current = txt;
        sendInput("clipboard.set", { text: txt });
      } catch {
        // Permission denied / not focused — ignore, will retry next tick
      }
    }

    // Poll every 1.5s while document is focused. Some browsers only allow
    // clipboard reads when the tab is foregrounded.
    const id = setInterval(() => {
      if (document.hasFocus()) pushIfChanged();
    }, 1500);

    // Also push immediately when the window regains focus or the user
    // explicitly pastes (paste-event always grants clipboard access).
    function onFocus() { pushIfChanged(); }
    function onPaste(e) {
      const txt = e.clipboardData?.getData("text/plain");
      if (!txt) return;
      if (txt === lastClipboardRef.current) return;
      lastClipboardRef.current = txt;
      sendInput("clipboard.set", { text: txt });
    }

    window.addEventListener("focus", onFocus);
    window.addEventListener("paste", onPaste);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("paste", onPaste);
    };
  }, [sendInput]);

  // ── Clipboard outbound sync ──
  // Poll the local clipboard for changes whenever the window is focused.
  // navigator.clipboard.readText() needs user activation + focus, so we
  // poll on focus events and a slow interval (500ms) when focused.
  useEffect(() => {
    if (!clipboardEnabled || !sendInput) return;
    let mounted = true;
    let interval = null;

    async function pollClipboard() {
      if (!mounted) return;
      if (!document.hasFocus()) return;
      if (!navigator.clipboard?.readText) return;
      try {
        const text = await navigator.clipboard.readText();
        if (typeof text !== "string") return;
        // Skip if the text is identical to what we just sent OR what we
        // just received from the host — prevents bounce loops.
        if (text === lastSentClipRef.current) return;
        if (text === lastReceivedClipRef.current) return;
        // Also skip empty payloads to avoid spamming the channel
        if (!text) return;
        lastSentClipRef.current = text;
        sendInput("clipboard.set", { text });
        setClipboardStatus("synced");
      } catch (err) {
        // Permission denied / not focused — silently retry next tick
      }
    }

    function onFocus() { pollClipboard(); }
    window.addEventListener("focus", onFocus);
    interval = setInterval(pollClipboard, 500);
    return () => {
      mounted = false;
      window.removeEventListener("focus", onFocus);
      if (interval) clearInterval(interval);
    };
  }, [clipboardEnabled, sendInput]);

  // ── Coordinate mapping ──
  const lastSentPosRef = useRef({ x: -1, y: -1 });
  const localToRemote = useCallback((e) => {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const rect = c.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) / scale);
    const y = Math.round((e.clientY - rect.top) / scale);
    return { x: Math.max(0, Math.min(remoteWidth, x)), y: Math.max(0, Math.min(remoteHeight, y)) };
  }, [scale, remoteWidth, remoteHeight]);

  // ── Input handlers ──
  const handleMouseMove = useCallback((e) => {
    // Reveal toolbar if mouse near top
    if (e.clientY < 80) {
      setShowToolbar(true);
      clearTimeout(toolbarHideTimerRef.current);
    } else if (!toolbarPinned && !showDrawer) {
      clearTimeout(toolbarHideTimerRef.current);
      toolbarHideTimerRef.current = setTimeout(() => setShowToolbar(false), 1800);
    }

    if (!sendInput) return;
    const now = performance.now();
    if (now - lastMouseMoveRef.current < 4) return; // throttle ~240Hz
    lastMouseMoveRef.current = now;
    const { x, y } = localToRemote(e);
    // Skip duplicate positions — saves bandwidth and CPU
    if (x === lastSentPosRef.current.x && y === lastSentPosRef.current.y) return;
    lastSentPosRef.current = { x, y };
    sendInput("browser.input", { kind: "mouse_move", x, y });
  }, [sendInput, localToRemote, toolbarPinned, showDrawer]);

  const handleMouseDown = useCallback((e) => {
    if (!sendInput) return;
    e.preventDefault();
    setShowInputHint(false);
    wrapperRef.current?.focus();
    const { x, y } = localToRemote(e);
    // Only send move if position changed since last move event
    if (x !== lastSentPosRef.current.x || y !== lastSentPosRef.current.y) {
      lastSentPosRef.current = { x, y };
      sendInput("browser.input", { kind: "mouse_move", x, y });
    }
    sendInput("browser.input", { kind: "mouse_btn", button: e.button, down: true });
  }, [sendInput, localToRemote]);

  const handleMouseUp = useCallback((e) => {
    if (!sendInput) return;
    e.preventDefault();
    sendInput("browser.input", { kind: "mouse_btn", button: e.button, down: false });
  }, [sendInput]);

  const handleWheel = useCallback((e) => {
    if (!sendInput) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 120 : -120;
    sendInput("browser.input", { kind: "mouse_wheel", delta });
  }, [sendInput]);

  const handleKeyDown = useCallback((e) => {
    if (!sendInput) return;
    e.preventDefault();
    sendInput("browser.input", { kind: "key", key_vk: e.keyCode, down: true });
  }, [sendInput]);

  const handleKeyUp = useCallback((e) => {
    if (!sendInput) return;
    e.preventDefault();
    sendInput("browser.input", { kind: "key", key_vk: e.keyCode, down: false });
  }, [sendInput]);

  // ── Special-key sequences ──
  function sendKeySequence(vks) {
    if (!sendInput) return;
    for (const vk of vks) sendInput("browser.input", { kind: "key", key_vk: vk, down: true });
    for (let i = vks.length - 1; i >= 0; i--) sendInput("browser.input", { kind: "key", key_vk: vks[i], down: false });
  }
  const sendCtrlAltDel  = () => sendKeySequence([17, 18, 46]);
  const sendWinKey      = () => sendKeySequence([91]);
  const sendAltTab      = () => sendKeySequence([18, 9]);
  const sendCtrlEsc     = () => sendKeySequence([17, 27]);
  const sendPrintScreen = () => sendKeySequence([44]);
  const sendEsc         = () => sendKeySequence([27]);

  function pushConfig(partial) {
    if (!sendInput) return;
    sendInput("browser.config", partial);
  }

  function toggleFullscreen() {
    const el = wrapperRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  }

  function toggleDisplay(idx) {
    let next;
    if (activeDisplays.includes(idx)) {
      next = activeDisplays.filter((d) => d !== idx);
      if (next.length === 0) next = [0]; // never disable everything
    } else {
      next = [...activeDisplays, idx].sort((a, b) => a - b);
    }
    setActiveDisplays(next);
    pushConfig({ displays: next });
  }

  // Track fullscreen state for the toolbar button icon
  useEffect(() => {
    function onFsChange() {
      setIsFullscreen(!!document.fullscreenElement);
    }
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  return (
    <div
      ref={wrapperRef}
      style={{
        width: "100%", height: "100%",
        position: "relative",
        background: "#000",
        outline: "none",
        overflow: "hidden",
      }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onContextMenu={(e) => e.preventDefault()}
      onMouseLeave={() => { if (!toolbarPinned && !showDrawer) setShowToolbar(false); }}
    >
      <div
        ref={containerRef}
        style={{
          width: "100%", height: "100%",
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: fitMode === "actual" ? "auto" : "hidden",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "center",
            background: "#000",
            imageRendering: "auto",
            cursor: remoteCursor === "none" ? "none" : remoteCursor,
          }}
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onWheel={handleWheel}
        />
      </div>

      {/* Toolbar — auto-hides, slides in from top */}
      <div
        style={{
          position: "absolute", top: 0, left: 0, right: 0,
          display: "flex", justifyContent: "center",
          pointerEvents: "none",
          zIndex: 20,
        }}
        onMouseEnter={() => { setShowToolbar(true); clearTimeout(toolbarHideTimerRef.current); }}
      >
        <div
          style={{
            display: "flex", gap: 6, alignItems: "center",
            background: "rgba(15,15,18,0.88)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderTop: "none",
            padding: "5px 8px",
            borderRadius: "0 0 10px 10px",
            fontSize: 11,
            fontFamily: "var(--mono, ui-monospace, monospace)",
            color: "rgba(255,255,255,0.85)",
            transform: showToolbar ? "translateY(0)" : "translateY(-100%)",
            transition: "transform 220ms cubic-bezier(0.4, 0, 0.2, 1)",
            pointerEvents: "auto",
            boxShadow: showToolbar ? "0 8px 24px rgba(0,0,0,0.35)" : "none",
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <ToolbarBtn onClick={sendCtrlAltDel} title="Ctrl+Alt+Del">Ctrl+Alt+Del</ToolbarBtn>
          <ToolbarBtn onClick={sendWinKey} title="Windows key">⊞</ToolbarBtn>
          <ToolbarBtn onClick={sendAltTab} title="Alt+Tab">Alt+Tab</ToolbarBtn>
          <ToolbarBtn onClick={sendCtrlEsc} title="Ctrl+Esc (Start menu)">Start</ToolbarBtn>
          <ToolbarBtn onClick={sendEsc} title="Escape">Esc</ToolbarBtn>
          <ToolbarBtn onClick={sendPrintScreen} title="Print Screen">PrtSc</ToolbarBtn>
          <ToolbarDivider />
          <ToolbarBtn
            onClick={() => setClipboardEnabled((v) => !v)}
            title={clipboardEnabled ? "Clipboard sync ON — click to disable" : "Clipboard sync OFF — click to enable"}
            active={clipboardEnabled}
          >
            📋
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => setFitMode((m) => m === "fit" ? "actual" : "fit")}
            title={fitMode === "fit" ? "Switch to 1:1 (scrollable)" : "Switch to fit-to-window"}
          >
            {fitMode === "fit" ? "1:1" : "Fit"}
          </ToolbarBtn>
          <ToolbarBtn
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
            active={isFullscreen}
          >
            {isFullscreen ? "⤓" : "⛶"}
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => setShowDrawer((v) => !v)}
            title="Stream settings (live)"
            active={showDrawer}
          >
            ⚙
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => setToolbarPinned((v) => !v)}
            title={toolbarPinned ? "Unpin toolbar" : "Pin toolbar (always visible)"}
            active={toolbarPinned}
          >
            {toolbarPinned ? "📌" : "📍"}
          </ToolbarBtn>
          <ToolbarBtn
            onClick={async () => {
              // Manual clipboard push — useful when auto-sync is blocked by
              // the browser's clipboard permissions.
              if (!navigator.clipboard || !sendInput) return;
              try {
                const txt = await navigator.clipboard.readText();
                if (txt) {
                  lastClipboardRef.current = txt;
                  sendInput("clipboard.set", { text: txt });
                }
              } catch {}
            }}
            title="Push local clipboard to remote"
          >
            📋
          </ToolbarBtn>
          <ToolbarDivider />
          <span style={{ color: "rgba(255,255,255,0.55)", padding: "0 4px", whiteSpace: "nowrap", fontSize: 10 }}>
            {fps} fps
          </span>
          <span
            title="Connection is TLS-encrypted (WSS) and authenticated"
            style={{ display: "flex", alignItems: "center", gap: 4, color: "#4ade80", padding: "0 6px", fontSize: 11 }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 4px #4ade80" }} />
            TLS
          </span>
        </div>
      </div>

      {/* Edge hover-zone — reveals hidden toolbar */}
      {!showToolbar && (
        <div
          style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 12,
            zIndex: 19,
          }}
          onMouseEnter={() => { setShowToolbar(true); clearTimeout(toolbarHideTimerRef.current); }}
        />
      )}

      {/* Settings drawer — slides in from the right */}
      <div
        style={{
          position: "absolute",
          top: 0, bottom: 0, right: 0,
          width: 320,
          background: "rgba(15,15,18,0.96)",
          backdropFilter: "blur(14px)",
          borderLeft: "1px solid rgba(255,255,255,0.08)",
          padding: "22px 18px 18px",
          fontSize: 11,
          color: "rgba(255,255,255,0.85)",
          fontFamily: "var(--mono, ui-monospace, monospace)",
          zIndex: 30,
          transform: showDrawer ? "translateX(0)" : "translateX(105%)",
          transition: "transform 260ms cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: showDrawer ? "-12px 0 32px rgba(0,0,0,0.55)" : "none",
          overflowY: "auto",
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ fontSize: 11, opacity: 0.55, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Stream Settings
          </div>
          <button
            onClick={() => setShowDrawer(false)}
            title="Close"
            style={{
              background: "transparent", border: "none", color: "rgba(255,255,255,0.6)",
              fontSize: 18, cursor: "pointer", padding: "0 4px",
            }}
          >×</button>
        </div>

        <div style={{ fontSize: 10, opacity: 0.5, marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Presets
        </div>
        <div style={{ display: "flex", gap: 4, marginBottom: 18 }}>
          <PresetBtn label="Performance" sub="60fps · q50" onClick={() => { pushConfig({ mode: "performance" }); setStreamFps(60); setStreamQuality(50); }} />
          <PresetBtn label="Balanced" sub="30fps · q70" onClick={() => { pushConfig({ mode: "balanced" }); setStreamFps(30); setStreamQuality(70); }} />
          <PresetBtn label="Quality" sub="30fps · q85" onClick={() => { pushConfig({ mode: "quality" }); setStreamFps(30); setStreamQuality(85); }} />
        </div>

        <div style={{ fontSize: 10, opacity: 0.5, marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Manual
        </div>
        <SliderRow
          label="Target FPS"
          value={streamFps}
          min={5} max={144} step={5}
          onChange={(v) => { setStreamFps(v); pushConfig({ fps: v }); }}
          unit="fps"
        />
        <SliderRow
          label="Quality"
          value={streamQuality}
          min={10} max={95} step={5}
          onChange={(v) => { setStreamQuality(v); pushConfig({ quality: v }); }}
          unit="%"
        />

        {monitors.length > 1 && (
          <>
            <div style={{ fontSize: 10, opacity: 0.5, marginBottom: 6, marginTop: 14, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Monitors
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 4 }}>
              {monitors.map((m) => {
                const active = activeDisplays.includes(m.index);
                return (
                  <button
                    key={m.index}
                    onClick={() => toggleDisplay(m.index)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: 8, padding: "8px 10px",
                      background: active ? "rgba(99,102,241,0.18)" : "rgba(255,255,255,0.03)",
                      border: active ? "1px solid rgba(99,102,241,0.5)" : "1px solid rgba(255,255,255,0.08)",
                      color: "rgba(255,255,255,0.85)",
                      borderRadius: 5,
                      cursor: "pointer",
                      fontSize: 11,
                      fontFamily: "inherit",
                      transition: "all 0.12s",
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        width: 14, height: 10,
                        border: "1.5px solid currentColor",
                        borderRadius: 2,
                        opacity: active ? 1 : 0.4,
                      }} />
                      Monitor {m.index + 1}
                    </span>
                    <span style={{ fontSize: 10, opacity: 0.55 }}>
                      {m.width}×{m.height}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", lineHeight: 1.5, marginBottom: 4 }}>
              Multiple monitors stitch horizontally.
            </div>
          </>
        )}

        <div style={{ marginTop: 14, padding: 10, background: "rgba(255,255,255,0.03)", borderRadius: 5, fontSize: 10, color: "rgba(255,255,255,0.55)", lineHeight: 1.6 }}>
          Higher FPS → smoother but more bandwidth.<br/>
          Quality controls JPEG compression.<br/>
          Changes apply live, no reconnect.
        </div>

        <div style={{ marginTop: 22, fontSize: 10, opacity: 0.5, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
          Live Stats
        </div>
        <div style={{ padding: 10, background: "rgba(255,255,255,0.03)", borderRadius: 5, fontFamily: "var(--mono, ui-monospace, monospace)", fontSize: 11 }}>
          <Stat label="Rendered" value={`${fps} fps`} />
          <Stat label="Resolution" value={`${remoteWidth}×${remoteHeight}`} />
          <Stat label="Scale" value={`${(scale * 100).toFixed(0)}%`} />
          <Stat label="Cursor" value={remoteCursor} />
        </div>

        <div style={{ marginTop: 22, fontSize: 10, opacity: 0.5, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
          Security
        </div>
        <div style={{ padding: 10, background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.18)", borderRadius: 5, fontSize: 10, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>
          <span style={{ color: "#4ade80" }}>● TLS encrypted</span><br/>
          Session is end-to-server encrypted via WSS.<br/>
          Authenticated with rotating refresh tokens.
        </div>
      </div>

      {showInputHint && (
        <div style={{
          position: "absolute",
          bottom: 16, left: "50%",
          transform: "translateX(-50%)",
          fontFamily: "var(--mono, ui-monospace, monospace)",
          fontSize: 11,
          color: "var(--acc, #6366f1)",
          background: "rgba(0,0,0,0.7)",
          padding: "8px 14px",
          borderRadius: 4,
          letterSpacing: "0.06em",
          pointerEvents: "none",
        }}>
          Click to start · Move mouse to top for toolbar
        </div>
      )}
    </div>
  );
}

function ToolbarBtn({ children, onClick, title, active }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: active ? "rgba(99,102,241,0.25)" : "transparent",
        border: active ? "1px solid rgba(99,102,241,0.5)" : "1px solid rgba(255,255,255,0.07)",
        color: "rgba(255,255,255,0.9)",
        padding: "4px 9px",
        fontSize: 11,
        fontFamily: "inherit",
        borderRadius: 4,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "background 0.12s, border-color 0.12s",
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div style={{ width: 1, height: 16, background: "rgba(255,255,255,0.1)", margin: "0 2px" }} />;
}

function PresetBtn({ label, sub, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        color: "rgba(255,255,255,0.85)",
        padding: "8px 6px",
        fontSize: 10,
        borderRadius: 5,
        cursor: "pointer",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        fontFamily: "inherit",
        transition: "background 0.12s, border-color 0.12s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(99,102,241,0.18)"; e.currentTarget.style.borderColor = "rgba(99,102,241,0.4)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}
    >
      <span style={{ fontWeight: 600 }}>{label}</span>
      {sub && <span style={{ fontSize: 9, opacity: 0.55, textTransform: "none", letterSpacing: 0 }}>{sub}</span>}
    </button>
  );
}

function SliderRow({ label, value, min, max, step, onChange, unit }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 11 }}>
        <span style={{ opacity: 0.7 }}>{label}</span>
        <span style={{ fontFamily: "var(--mono, ui-monospace, monospace)", color: "var(--acc, #6366f1)" }}>
          {value}{unit ? " " + unit : ""}
        </span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        style={{ width: "100%", accentColor: "var(--acc, #6366f1)" }}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 11 }}>
      <span style={{ opacity: 0.55 }}>{label}</span>
      <span style={{ color: "var(--acc, #6366f1)" }}>{value}</span>
    </div>
  );
}
